import { IAuthenticationManager } from './IAuthenticationManager'
import { IUser } from './IUser'
import { Amplify, Auth } from 'aws-amplify'
import { CognitoUser } from '@aws-amplify/auth'
import { CognitoUserSession, CognitoIdToken } from 'amazon-cognito-identity-js'
import { ISession } from './ISession'
import { IIdToken } from './IIdToken'
import { ISignUpResponse } from './ISignUpResponse'

class CognitoUserWrapper implements IUser {
  constructor(
    public underlyingUserObject: CognitoUser & { attributes?: Record<string, object>; username?: string; displayName?: string }
  ) {}

  public get attributes(): Record<string, any> | undefined {
    return this.underlyingUserObject.attributes
  }

  public get username(): string {
    return this.underlyingUserObject.username ?? this.underlyingUserObject.getUsername()
  }

  public get displayName(): string | undefined {
    return this.underlyingUserObject.displayName
  }
}

class CognitoSessionWrapper implements ISession {
  constructor(private underlyingSession: CognitoUserSession) {}

  getIdToken(): IIdToken {
    return new CognitoIdTokenWrapper(this.underlyingSession.getIdToken())
  }
}

class CognitoIdTokenWrapper implements IIdToken {
  constructor(private underlyingIdToken: CognitoIdToken) {}

  get payload(): { [key: string]: any } {
    return this.underlyingIdToken.payload
  }

  getJwtToken(): string {
    return this.underlyingIdToken.getJwtToken()
  }

  getExpiration(): number {
    return this.underlyingIdToken.getExpiration()
  }

  getIssuedAt(): number {
    return this.underlyingIdToken.getIssuedAt()
  }
}

export class CognitoAuthenticationManager implements IAuthenticationManager {
  constructor(config: any) {
    Auth.configure(config)
    Amplify.configure(config)
  }

  async currentAuthenticatedUser(): Promise<IUser | undefined> {
    let user: any
    try {
      user = await Auth.currentAuthenticatedUser()
    } catch (e) {
      // Ignore the standard return value if the user is not authenticated
      if (e !== 'The user is not authenticated') {
        console.warn(e)
      }
    }
    if (user && !(user instanceof CognitoUser)) {
      console.warn('currentAuthenticatedUser returned an unexpected object', user)
    }
    return user ? new CognitoUserWrapper(user) : undefined
  }

  async currentSession(): Promise<ISession> {
    return new CognitoSessionWrapper(await Auth.currentSession())
  }

  async signIn(email: string, password: string): Promise<IUser> {
    return new CognitoUserWrapper(await Auth.signIn(email, password))
  }

  signOut(): Promise<void> {
    return Auth.signOut()
  }

  async changePassword(user: IUser, oldPassword: string, newPassword: string): Promise<void> {
    await Auth.changePassword(user.underlyingUserObject, oldPassword, newPassword)
  }

  accountManager = {
    forgotPassword(email: string): Promise<void> {
      return Auth.forgotPassword(email)
    },

    forgotPasswordSubmit(email: string, code: string, password: string): Promise<string> {
      return Auth.forgotPasswordSubmit(email, code, password)
    },

    async signUp(options: { username: string; password: string }): Promise<ISignUpResponse> {
      const result = await Auth.signUp(options)
      return {
        user: new CognitoUserWrapper(result.user),
        userId: result.userSub,
      }
    },
  }
}
